import { LEVEL_COLORS, STATIC_RATING_SCALES } from './constants'
import { BackendLabel, FieldErrors, RatingScale } from './types'

export const createId = () =>
  crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)

export const normalizeBoolean = (
  value: boolean | string | undefined,
  fallback: boolean
) => {
  if (typeof value === 'boolean') return value

  if (typeof value === 'string') {
    return value === 'true' || value === '1'
  }

  return fallback
}

export const normalizeColor = (value?: string) => {
  if (!value) return '#39C674'
  return value.startsWith('#') ? value : `#${value}`
}

const BASE_SCALE_LABELS: Record<number, string> = {
  [-1]: 'Working Towards',
  [0]: 'Meeting',
  [1]: 'Exceeding',
}

// Values that can be added below the core (closest to center first)
export const BELOW_EXTRA_VALUES = [-2, -3]
// Values that can be added above the core (closest to center first)
export const ABOVE_EXTRA_VALUES = [2, 3]

export const convertBackendToFrontend = (
  labels: BackendLabel[]
): RatingScale[] =>
  labels.map((label) => {
    const baseLabel = BASE_SCALE_LABELS[label.scale_value]
    return {
      id: createId(),
      kind: baseLabel ? 'mapped' : 'extra',
      label: label.label_name,
      description: label.description,
      color: normalizeColor(label.color),
      scaleValue: label.scale_value,
      ...(baseLabel ? { baseLabel } : {}),
    }
  })

export const convertFrontendToBackend = (
  scales: RatingScale[]
): BackendLabel[] =>
  scales.map((scale) => ({
    label_name: scale.label,
    description: scale.description,
    color: scale.color,
    scale_value: scale.scaleValue,
  }))

export const buildInitialSavedScales = (
  initialScalesData?: string
): RatingScale[] => {
  if (initialScalesData) {
    try {
      const labels: BackendLabel[] = JSON.parse(initialScalesData)
      if (Array.isArray(labels) && labels.length > 0) {
        return convertBackendToFrontend(labels)
      }
    } catch {
      // fall through to static defaults
    }
  }

  return STATIC_RATING_SCALES.map((scale) => ({
    ...scale,
    id: createId(),
  }))
}

export const cloneScales = (scales: RatingScale[]): RatingScale[] =>
  scales.map((scale) => ({
    ...scale,
  }))

const LABEL_NAME_FORMAT = /^[a-zA-Z][a-zA-Z0-9\s\-_'.&/+]*$/

export const validateScales = (scales: RatingScale[]): FieldErrors => {
  const nextErrors: FieldErrors = {}

  const normalizedNames = scales.map((s) => s.label.trim().toLowerCase())
  const duplicateNames = new Set(
    normalizedNames.filter(
      (name, i) => name && normalizedNames.indexOf(name) !== i
    )
  )

  scales.forEach((scale) => {
    const label = scale.label.trim()
    const description = scale.description.trim()

    if (!label) {
      nextErrors[scale.id] = {
        ...nextErrors[scale.id],
        label: 'Label is required',
      }
    } else if (label.length > 32) {
      nextErrors[scale.id] = {
        ...nextErrors[scale.id],
        label: 'Label must be 32 characters or fewer',
      }
    } else if (!LABEL_NAME_FORMAT.test(label)) {
      nextErrors[scale.id] = {
        ...nextErrors[scale.id],
        label:
          "Label must start with a letter and contain only letters, numbers, spaces, or - _ ' . & / +",
      }
    } else if (duplicateNames.has(label.toLowerCase())) {
      nextErrors[scale.id] = {
        ...nextErrors[scale.id],
        label: 'Label name must be unique',
      }
    }

    const descriptionText = description.replace(/<[^>]*>/g, '').trim()
    if (!descriptionText) {
      nextErrors[scale.id] = {
        ...nextErrors[scale.id],
        description: 'Description is required',
      }
    }
  })

  return nextErrors
}

const buildNewExtraScaleForValues = (
  scales: RatingScale[],
  candidates: number[]
): RatingScale | null => {
  const usedValues = new Set(scales.map((s) => s.scaleValue))
  const scaleValue = candidates.find((v) => !usedValues.has(v))
  if (scaleValue === undefined) return null

  return {
    id: createId(),
    kind: 'extra',
    label: '',
    description: '',
    color: LEVEL_COLORS[scaleValue] ?? '#2E63B6',
    scaleValue,
  }
}

export const buildNewBelowScale = (scales: RatingScale[]): RatingScale | null =>
  buildNewExtraScaleForValues(scales, BELOW_EXTRA_VALUES)

export const buildNewAboveScale = (scales: RatingScale[]): RatingScale | null =>
  buildNewExtraScaleForValues(scales, ABOVE_EXTRA_VALUES)

export const compactExtraScales = (scales: RatingScale[]): RatingScale[] => {
  const usedValues = new Set(scales.map((s) => s.scaleValue))
  const belowInner = BELOW_EXTRA_VALUES[0]
  const belowOuter = BELOW_EXTRA_VALUES[1]
  const aboveInner = ABOVE_EXTRA_VALUES[0]
  const aboveOuter = ABOVE_EXTRA_VALUES[1]

  return scales.map((scale) => {
    if (scale.scaleValue === belowOuter && !usedValues.has(belowInner)) {
      return { ...scale, scaleValue: belowInner }
    }
    if (scale.scaleValue === aboveOuter && !usedValues.has(aboveInner)) {
      return { ...scale, scaleValue: aboveInner }
    }
    return scale
  })
}

export const trimScales = (scales: RatingScale[]): RatingScale[] =>
  scales.map((scale) => ({
    ...scale,
    label: scale.label.trim(),
    description: scale.description.trim(),
  }))

export const reorderScales = (
  scales: RatingScale[],
  sourceId: string,
  targetId: string
) => {
  if (sourceId === targetId) return scales

  const sourceIndex = scales.findIndex((scale) => scale.id === sourceId)
  const targetIndex = scales.findIndex((scale) => scale.id === targetId)

  if (sourceIndex === -1 || targetIndex === -1) return scales

  const next = [...scales]
  const [moved] = next.splice(sourceIndex, 1)
  next.splice(targetIndex, 0, moved)

  return next
}
